import { NumericAggregateMetricRecord } from "@/shared/domain/metrics";

export type TrendDirection = "up" | "down" | "flat";

export interface Trend {
  /** Value of the most recent bucket, or `null` when there are no buckets. */
  current: number | null;
  /** Value of the bucket before the most recent, or `null` when <2 buckets. */
  previous: number | null;
  /** Percent change current-vs-previous, or `null` when not computable
   *  (<2 buckets, or previous === 0). */
  percentChange: number | null;
  direction: TrendDirection;
}

// Treat sub-epsilon deltas as flat to avoid flapping arrows on float noise.
const EPSILON = 1e-9;

/**
 * Reduce a single metric's aggregate buckets to a "current vs previous period"
 * trend. The aggregate endpoint returns one record per bucket, so "current" is
 * the last bucket and "previous" is the one before it. Input order is not
 * assumed — records are sorted by `start_time` first.
 */
export function computeTrend(
  records: NumericAggregateMetricRecord[] | undefined,
): Trend {
  if (!records || records.length === 0) {
    return {
      current: null,
      previous: null,
      percentChange: null,
      direction: "flat",
    };
  }

  const sorted = [...records].sort((a, b) => a.start_time - b.start_time);
  const current = sorted[sorted.length - 1].value;

  if (sorted.length < 2) {
    return { current, previous: null, percentChange: null, direction: "flat" };
  }

  const previous = sorted[sorted.length - 2].value;
  const diff = current - previous;
  const percentChange =
    previous === 0 ? null : (diff / Math.abs(previous)) * 100;

  let direction: TrendDirection = "flat";
  if (diff > EPSILON) {
    direction = "up";
  } else if (diff < -EPSILON) {
    direction = "down";
  }

  return { current, previous, percentChange, direction };
}
